import { Mood, SecondaryMood } from '@peekapak/types/Mood';
import { GradeBand } from '@peekapak/types/EmotionsClassification';
import { EmotionsReportCheckin } from '@peekapak/types/EmotionsReportRow';

type Feeling = Mood | SecondaryMood;

function formatShortDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export interface WeekBucket {
  weekLabel: string;
  counts: Partial<Record<Feeling, number>>;
  dates: Partial<Record<Feeling, string[]>>;
}

// Weeks are day-of-month buckets (1-7, 8-14, 15-21, 22-end), not calendar weeks.
export function buildWeeklyChartData(checkins: EmotionsReportCheckin[], year: number, month: number): WeekBucket[] {
  const monthStart = new Date(year, month, 1).getTime();
  const monthEnd = new Date(year, month + 1, 1).getTime();
  const inMonth = checkins.filter((checkin) => checkin.createdAt >= monthStart && checkin.createdAt < monthEnd);

  const weeks: WeekBucket[] = [1, 2, 3, 4].map((weekNumber) => ({
    weekLabel: `Week ${weekNumber}`,
    counts: {},
    dates: {},
  }));

  for (const checkin of inMonth) {
    const dayOfMonth = new Date(checkin.createdAt).getDate();
    const weekIndex = Math.min(3, Math.floor((dayOfMonth - 1) / 7));
    const feeling = checkin.feeling;
    const bucket = weeks[weekIndex];
    bucket.counts[feeling] = (bucket.counts[feeling] ?? 0) + 1;
    bucket.dates[feeling] = [...(bucket.dates[feeling] ?? []), formatShortDate(checkin.createdAt)];
  }

  return weeks;
}

export interface PeriodPercentage {
  feeling: Feeling;
  percentage: number;
}

// Percentages round independently per tile, so they may not sum to exactly 100%.
// Only feelings that actually occurred get a tile - no fixed grade-band palette.
export function buildPeriodPercentages(
  checkins: EmotionsReportCheckin[],
  year: number,
  month: number,
): PeriodPercentage[] {
  const monthStart = new Date(year, month, 1).getTime();
  const monthEnd = new Date(year, month + 1, 1).getTime();
  const inMonth = checkins.filter((checkin) => checkin.createdAt >= monthStart && checkin.createdAt < monthEnd);

  const total = inMonth.length;
  const counts = new Map<Feeling, number>();

  for (const checkin of inMonth) {
    counts.set(checkin.feeling, (counts.get(checkin.feeling) ?? 0) + 1);
  }

  return [...counts.entries()]
    .sort(([, a], [, b]) => b - a)
    .map(([feeling, count]) => ({
      feeling,
      percentage: Math.round((count / total) * 100),
    }));
}

export function buildRecentCheckins(checkins: EmotionsReportCheckin[]): EmotionsReportCheckin[] {
  return [...checkins].sort((a, b) => b.createdAt - a.createdAt);
}
